import { ObservabilityLogLevel } from '@inditex/core-o11y-js';
import { sendLog } from 'src/utils/observability/observabilityUtils';
import { getAppEnviroment, isAndroid, isIOS } from 'src/utils/utils';

const IMAGE_INTEGRITY_LABEL = 'Image Integrity';

type IntegrityProcess = 'antes_de_subir' | 'despues_de_subir';

type SnapshotFile = Blob | File;

type SnapshotParams = {
  message: string;
  step: string;
  file?: SnapshotFile;
  index?: number;
  assetId?: string;
  uri?: string;
  source?: string;
  operation?: string;
  level?: ObservabilityLogLevel;
  extraTags?: Record<string, string>;
};

type TransformationParams = {
  index: number;
  inputFile: File;
  outputFile: File;
  operation: string;
  extraTags?: Record<string, string>;
};

function formatExtraTagsForMessage(extraTags: Record<string, string>): string {
  const entries = Object.entries(extraTags);
  if (entries.length === 0) return '';
  return ' | ' + entries.map(([key, value]) => `${key}=${value}`).join(', ');
}

function getPlatform(): string {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'web';
}

function getCommonTags(index?: number, assetId?: string, uri?: string): Record<string, string> {
  return {
    platform: getPlatform(),
    app_environment: getAppEnviroment() || 'browser',
    index: typeof index === 'number' ? String(index) : 'without_index',
    is_first_image: String(index === 0),
    asset_id: assetId || 'without_asset_id',
    assetId: assetId || 'without_asset_id',
    uri_scheme: uri ? getUriScheme(uri) : 'without_uri',
    uri_host: uri ? extractHost(uri) : 'without_uri',
    uri: uri || '',
  };
}

function getBytePreview(buffer: ArrayBuffer): { firstBytes: string; lastBytes: string } {
  const bytes = new Uint8Array(buffer);
  const previewLength = Math.min(12, bytes.length);
  const firstBytes = Array.from(bytes.slice(0, previewLength))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  const lastBytes = Array.from(bytes.slice(Math.max(bytes.length - previewLength, 0)))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

  return { firstBytes: firstBytes || 'empty', lastBytes: lastBytes || 'empty' };
}

async function getImageDimensions(file: SnapshotFile): Promise<{ width: string; height: string }> {
  const url = URL.createObjectURL(file);

  try {
    const image = new Image();
    image.src = url;

    if (typeof image.decode === 'function') {
      await image.decode();
    } else {
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('image_load_error'));
      });
    }

    return {
      width: String(image.naturalWidth || image.width || 0),
      height: String(image.naturalHeight || image.height || 0),
    };
  } catch {
    return { width: 'unknown', height: 'unknown' };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function getFileTags(file?: SnapshotFile): Promise<Record<string, string>> {
  if (!file) {
    return {
      file_name: 'without_file',
      file_size: '0',
      file_type: 'empty',
      file_sha256: 'not_computed',
      file_md5: 'not_computed',
      file_first_bytes: 'empty',
      file_last_bytes: 'empty',
      image_width: 'unknown',
      image_height: 'unknown',
    };
  }

  const buffer = await file.arrayBuffer();
  const hashes = await computeHashes(buffer);
  const preview = getBytePreview(buffer);
  const dimensions = await getImageDimensions(file);
  const fileName = file instanceof File ? file.name : 'without_file_name';

  return {
    file_name: fileName,
    file_size: String(file.size),
    file_type: file.type || 'empty',
    file_sha256: hashes.sha256,
    file_md5: hashes.md5,
    file_first_bytes: preview.firstBytes,
    file_last_bytes: preview.lastBytes,
    image_width: dimensions.width,
    image_height: dimensions.height,
  };
}

export const logImageStepSnapshot = async ({
  message,
  step,
  file,
  index,
  assetId,
  uri,
  source = 'image_pipeline',
  operation = 'unknown',
  level = ObservabilityLogLevel.INFO,
  extraTags = {},
}: SnapshotParams): Promise<void> => {
  try {
    const fileTags = await getFileTags(file);

    sendLog({
      message: message + formatExtraTagsForMessage(extraTags),
      level,
      label: IMAGE_INTEGRITY_LABEL,
      tags: {
        source,
        step,
        operation,
        ...getCommonTags(index, assetId, uri),
        ...fileTags,
        ...extraTags,
      },
    });
  } catch (error) {
    sendLog({
      message: `${step} snapshot failed: ${error}`,
      level: ObservabilityLogLevel.ERROR,
      label: IMAGE_INTEGRITY_LABEL,
      tags: {
        source,
        step,
        operation,
        ...getCommonTags(index, assetId, uri),
        error: String(error),
      },
    });
  }
};

export const logConvertUriToFileFetch = async (
  uri: string,
  index: number,
  response: Response,
  process?: IntegrityProcess,
  assetId?: string,
): Promise<void> => {
  sendLog({
    message: `${process?.toLocaleUpperCase() || 'UNKNOWN_PROCESS'} - convertUriToFile fetch response`,
    label: IMAGE_INTEGRITY_LABEL,
    tags: {
      source: 'native_bridge',
      step: 'convertUriToFile_fetch',
      operation: 'fetch_uri',
      ...getCommonTags(index, assetId, uri),
      response_ok: String(response.ok),
      response_status: String(response.status),
      response_redirected: String(response.redirected),
      response_type: response.type || 'unknown',
      response_content_type: response.headers.get('content-type') || 'missing',
      response_content_length: response.headers.get('content-length') || 'missing',
      response_etag: response.headers.get('etag') || 'missing',
    },
  });
};

function bufferToHex(buffer: ArrayBuffer): string {
  const hashArray = Array.from(new Uint8Array(buffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function computeSha256(data: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return bufferToHex(hashBuffer);
}

async function computeMd5(data: ArrayBuffer): Promise<string> {
  // SparkMD5-free: use SubtleCrypto where available, fallback to simple hash.
  // Note: Web Crypto API does not support MD5 natively.
  // We compute a simple MD5-equivalent using a lightweight pure-JS implementation.
  const bytes = new Uint8Array(data);
  return md5(bytes);
}

/**
 * Minimal pure-JS MD5 (RFC 1321). No dependencies.
 * Operates on Uint8Array input, returns hex string.
 */
function md5(input: Uint8Array): string {
  function toUint32Array(bytes: Uint8Array): Uint32Array {
    const padded = md5Pad(bytes);
    const words = new Uint32Array(padded.length / 4);
    for (let i = 0; i < words.length; i++) {
      words[i] =
        padded[i * 4] |
        (padded[i * 4 + 1] << 8) |
        (padded[i * 4 + 2] << 16) |
        (padded[i * 4 + 3] << 24);
    }
    return words;
  }

  function md5Pad(bytes: Uint8Array): Uint8Array {
    const bitLen = bytes.length * 8;
    const newLen = bytes.length + 1 + ((55 - bytes.length % 64 + 64) % 64);
    const padded = new Uint8Array(newLen + 8);
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    padded[newLen] = bitLen & 0xff;
    padded[newLen + 1] = (bitLen >>> 8) & 0xff;
    padded[newLen + 2] = (bitLen >>> 16) & 0xff;
    padded[newLen + 3] = (bitLen >>> 24) & 0xff;
    // For files < 512MB, upper 32 bits of bit length are 0
    return padded;
  }

  const S: number[][] = [
    [7, 12, 17, 22], [5, 9, 14, 20], [4, 11, 16, 23], [6, 10, 15, 21],
  ];
  const K = new Uint32Array(64);
  for (let i = 0; i < 64; i++) {
    K[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 0x100000000) >>> 0;
  }

  const words = toUint32Array(input);
  let a0 = 0x67452301 >>> 0;
  let b0 = 0xefcdab89 >>> 0;
  let c0 = 0x98badcfe >>> 0;
  let d0 = 0x10325476 >>> 0;

  for (let offset = 0; offset < words.length; offset += 16) {
    let A = a0, B = b0, C = c0, D = d0;
    for (let i = 0; i < 64; i++) {
      let F: number, g: number;
      if (i < 16) {
        F = (B & C) | (~B & D);
        g = i;
      } else if (i < 32) {
        F = (D & B) | (~D & C);
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        F = B ^ C ^ D;
        g = (3 * i + 5) % 16;
      } else {
        F = C ^ (B | ~D);
        g = (7 * i) % 16;
      }
      F = ((F >>> 0) + A + K[i] + words[offset + g]) >>> 0;
      A = D;
      D = C;
      C = B;
      const s = S[Math.floor(i / 16)][i % 4];
      B = (B + (((F << s) | (F >>> (32 - s))) >>> 0)) >>> 0;
    }
    a0 = (a0 + A) >>> 0;
    b0 = (b0 + B) >>> 0;
    c0 = (c0 + C) >>> 0;
    d0 = (d0 + D) >>> 0;
  }

  function u32ToHex(v: number): string {
    return [v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff]
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  return u32ToHex(a0) + u32ToHex(b0) + u32ToHex(c0) + u32ToHex(d0);
}

async function computeHashes(data: ArrayBuffer): Promise<{ sha256: string; md5: string }> {
  const sha256 = await computeSha256(data);
  const md5Hash = await computeMd5(data);
  return { sha256, md5: md5Hash };
}

export async function computeMd5Base64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hexMd5 = await computeMd5(buffer);
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hexMd5.slice(i * 2, i * 2 + 2), 16);
  }
  return btoa(String.fromCharCode(...bytes));
}

function getUriScheme(uri: string): string {
  const colonIndex = uri.indexOf(':');
  if (colonIndex === -1) return 'unknown';
  return uri.substring(0, colonIndex);
}

export const logConvertUriToFileIntegrity = async (
  uri: string,
  index: number,
  blob: Blob,
  file: File,
  process?: IntegrityProcess,
  assetId?: string
): Promise<void> => {
  try {
    const blobBuffer = await blob.arrayBuffer();
    const fileBuffer = await file.arrayBuffer();
    const blobHashes = await computeHashes(blobBuffer);
    const fileHashes = await computeHashes(fileBuffer);
    const blobPreview = getBytePreview(blobBuffer);
    const filePreview = getBytePreview(fileBuffer);
    const blobDimensions = await getImageDimensions(blob);
    const fileDimensions = await getImageDimensions(file);
    const match = blobHashes.sha256 === fileHashes.sha256 && blobHashes.md5 === fileHashes.md5;

    sendLog({
      message: match
        ? `${process?.toLocaleUpperCase()} - integrity OK`
        : `${process?.toLocaleUpperCase()} - integrity MISMATCH`,
      level: match ? ObservabilityLogLevel.INFO : ObservabilityLogLevel.ERROR,
      label: IMAGE_INTEGRITY_LABEL,
      tags: {
        source: 'native_bridge',
        step: 'convertUriToFile',
        operation: 'blob_to_file',
        ...getCommonTags(index, assetId, uri),
        file_name: file.name,
        blob_size: String(blob.size),
        blob_type: blob.type || 'empty',
        blob_first_bytes: blobPreview.firstBytes,
        blob_last_bytes: blobPreview.lastBytes,
        blob_width: blobDimensions.width,
        blob_height: blobDimensions.height,
        file_size: String(file.size),
        file_type: file.type || 'empty',
        file_first_bytes: filePreview.firstBytes,
        file_last_bytes: filePreview.lastBytes,
        file_width: fileDimensions.width,
        file_height: fileDimensions.height,
        blob_sha256: blobHashes.sha256,
        blob_md5: blobHashes.md5,
        file_sha256: fileHashes.sha256,
        file_md5: fileHashes.md5,
        hashes_match: String(match),
      },
    });
  } catch (error) {
    sendLog({
      message: `${process} integrity check failed: ${error}`,
      level: ObservabilityLogLevel.ERROR,
      label: IMAGE_INTEGRITY_LABEL,
      tags: {
        source: 'native_bridge',
        step: process || 'UNKNOWN',
        operation: 'blob_to_file',
        ...getCommonTags(index, assetId, uri),
        file_name: file.name,
        error: String(error),
      },
    });
  }
};


export const logImageTransformationResult = async ({
  index,
  inputFile,
  outputFile,
  operation,
  extraTags = {},
}: TransformationParams): Promise<void> => {
  try {
    const inputBuffer = await inputFile.arrayBuffer();
    const outputBuffer = await outputFile.arrayBuffer();
    const inputHashes = await computeHashes(inputBuffer);
    const outputHashes = await computeHashes(outputBuffer);
    const inputPreview = getBytePreview(inputBuffer);
    const outputPreview = getBytePreview(outputBuffer);
    const inputDimensions = await getImageDimensions(inputFile);
    const outputDimensions = await getImageDimensions(outputFile);

    sendLog({
      message: `Image transformation ${operation}` + formatExtraTagsForMessage(extraTags),
      label: IMAGE_INTEGRITY_LABEL,
      tags: {
        source: 'image_pipeline',
        step: 'image_transformation',
        operation,
        ...getCommonTags(index),
        input_file_name: inputFile.name,
        input_file_size: String(inputFile.size),
        input_file_type: inputFile.type || 'empty',
        input_file_sha256: inputHashes.sha256,
        input_file_md5: inputHashes.md5,
        input_first_bytes: inputPreview.firstBytes,
        input_last_bytes: inputPreview.lastBytes,
        input_width: inputDimensions.width,
        input_height: inputDimensions.height,
        output_file_name: outputFile.name,
        output_file_size: String(outputFile.size),
        output_file_type: outputFile.type || 'empty',
        output_file_sha256: outputHashes.sha256,
        output_file_md5: outputHashes.md5,
        output_first_bytes: outputPreview.firstBytes,
        output_last_bytes: outputPreview.lastBytes,
        output_width: outputDimensions.width,
        output_height: outputDimensions.height,
        output_differs_from_input: String(
          inputHashes.sha256 !== outputHashes.sha256 || inputHashes.md5 !== outputHashes.md5,
        ),
        ...extraTags,
      },
    });
  } catch (error) {
    sendLog({
      message: `Image transformation ${operation} failed while logging: ${error}` + formatExtraTagsForMessage(extraTags),
      level: ObservabilityLogLevel.ERROR,
      label: IMAGE_INTEGRITY_LABEL,
      tags: {
        source: 'image_pipeline',
        step: 'image_transformation',
        operation,
        ...getCommonTags(index),
        input_file_name: inputFile.name,
        output_file_name: outputFile.name,
        error: String(error),
        ...extraTags,
      },
    });
  }
};

export const logImageTransformationError = async (
  index: number,
  file: File,
  operation: string,
  error: unknown,
  extraTags: Record<string, string> = {},
): Promise<void> => {
  await logImageStepSnapshot({
    message: `Image transformation ${operation} failed`,
    step: 'image_transformation_error',
    file,
    index,
    operation,
    source: 'image_pipeline',
    level: ObservabilityLogLevel.ERROR,
    extraTags: {
      error: String(error),
      ...extraTags,
    },
  });
};


function extractHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return 'invalid_url';
  }
}